import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import IntegrationIcon from "src/components/ui/IntegrationIcon";
import { getProductName } from "src/utils/brandTheme";

import type { RollbarIntegrationQuery } from "./__generated__/RollbarIntegrationQuery.graphql";
import RollbarIntegrationForm from "./RollbarIntegrationForm";

const rollbarIntegrationQuery = graphql`
  query RollbarIntegrationQuery {
    me {
      ...appMe
      organization {
        integrations(integrationType: ROLLBAR) {
          edges {
            node {
              id
              credentials
            }
          }
        }
      }
    }
  }
`;

interface RollbarIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
  initialAccountName?: string;
  // True when launched via "Connect Another" from the multi-integration dialog —
  // suppresses the existing-integration auto-lookup so we always create a new row.
  isConnectingNew?: boolean;
}

export const RollbarInstructions: React.FC<{ productName: string }> = ({ productName }) => {
  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">Account Name Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Open Rollbar — once signed in, your URL will look like{" "}
            <span className="text-amber-600">https://app.rollbar.com/a/&lt;account_name&gt;/p/&lt;project_name&gt;/...</span>
          </li>
          <li>
            The path segment after <span className="text-amber-600">/a/</span> is your <span className="text-amber-600">account_name</span>.
          </li>
          <li>
            Paste it into the Account Name field below. The project name will be auto-detected during validation.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Project Read Token Setup:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Go to the <span className="text-amber-600">Projects</span> tab — you&apos;ll see a list of projects. Click the project you want to integrate.
          </li>
          <li>
            Navigate to <span className="text-amber-600">Project Settings</span>
            {" > "}
            <span className="text-amber-600">Project Access Tokens</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Generate new access token</span>. Name it &quot;{productName} Integration&quot;.
          </li>
          <li>
            Grant the <span className="text-amber-600">read</span> scope only. Do not grant write or post scopes.
          </li>
          <li>
            Copy the token (Rollbar shows it once) and paste it into the Project Read Token field below.
          </li>
          <li>
            Repeat per Rollbar project — {productName} stores one integration per project.
          </li>
        </ol>
      </div>
    </div>
  );
};

export default function RollbarIntegration({ isOpen, onClose, onRefetch, brandTheme, initialAccountName, isConnectingNew }: RollbarIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  // Conditional hook pattern - matches PagerDutyIntegration / SentryIntegration
  const data = isOpen
    ? useLazyLoadQuery<RollbarIntegrationQuery>(
        rollbarIntegrationQuery,
        {},
        { fetchPolicy: "network-only" },
      )
    : null;

  // When launched via "Connect Another", skip the existing-integration lookup so
  // the form treats this as a fresh create (no integrationId → mutation hits the
  // create-new path, not the update-by-id path).
  const existingIntegration = isConnectingNew ? undefined : data?.me.organization?.integrations?.edges?.[0]?.node;
  const existingCredentials = existingIntegration?.credentials as {
    account_name?: string;
    project_read_token?: string;
  } | null;
  const isConnected = !!existingIntegration;

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  const handleFormCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <IntegrationIcon type="ROLLBAR" className="h-6 w-6" />
            {isConnected ? "Manage Rollbar" : "Connect Rollbar"}
          </DialogTitle>
          <DialogDescription>
            {isConnected
              ? `Update your Rollbar integration settings for ${productName}.`
              : `Connect your Rollbar account to give ${productName} access.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <RollbarInstructions productName={productName} />
            </div>
          </div>

          <RollbarIntegrationForm
            onComplete={handleFormComplete}
            onCancel={handleFormCancel}
            integrationId={existingIntegration?.id}
            initialAccountName={existingCredentials?.account_name ?? initialAccountName}
            initialProjectReadToken={existingCredentials?.project_read_token}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
